import React, { useEffect, useState } from "react";
import { Menu, X, ShoppingCart, Globe } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";

export default function Navbar() {
  const { t, S, lang, toggle } = useLang();
  const { count, setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bump, setBump] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (count > 0) {
      setBump(true);
      const id = setTimeout(() => setBump(false), 320);
      return () => clearTimeout(id);
    }
  }, [count]);

  const links = [
    { id: "servicios", label: t(S.nav.services) },
    { id: "destinos", label: t(S.nav.destinations) },
    { id: "tarifas", label: t(S.nav.transfers) },
    { id: "experiencias", label: t(S.nav.experiences) },
    { id: "contacto", label: t(S.nav.contact) },
  ];

  const dark = !scrolled;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5">
          <span className={`grid place-items-center w-9 h-9 rounded-xl font-display font-extrabold text-white ${scrolled ? "bg-primary" : "bg-white/20 backdrop-blur"}`}>
            X
          </span>
          <div className="leading-none">
            <span className={`font-display font-extrabold text-lg tracking-tight ${dark ? "text-white" : "text-primary"}`}>
              XPERIENCIAS
            </span>
            <span className={`block text-[11px] tracking-[0.22em] font-semibold ${dark ? "text-white/80" : "text-accent"}`}>
              MEXICO
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className={`text-sm font-medium transition-colors hover:text-accent ${dark ? "text-white/90" : "text-foreground"}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={toggle}
            className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg transition-colors ${
              dark ? "text-white hover:bg-white/15" : "text-foreground hover:bg-secondary"
            }`}
            aria-label="Toggle language"
          >
            <Globe className="w-4 h-4" />
            {lang.toUpperCase()}
          </button>

          <button
            onClick={() => setIsOpen(true)}
            className={`relative p-2.5 rounded-lg transition-colors ${dark ? "text-white hover:bg-white/15" : "text-foreground hover:bg-secondary"}`}
            aria-label="Open cart"
          >
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1 grid place-items-center rounded-full bg-accent text-accent-foreground text-[11px] font-bold ${bump ? "badge-pop" : ""}`}>
                {count}
              </span>
            )}
          </button>

          <Button
            asChild
            className="hidden md:inline-flex bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full px-5"
          >
            <a href="#reservar">{t(S.nav.book)}</a>
          </Button>

          <button
            className={`lg:hidden p-2 rounded-lg ${dark ? "text-white" : "text-foreground"}`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden mt-3 mx-4 rounded-2xl bg-white shadow-xl border border-border p-4">
          <nav className="flex flex-col">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setMobileOpen(false)}
                className="py-3 px-2 text-foreground font-medium border-b border-border last:border-0 hover:text-primary"
              >
                {l.label}
              </a>
            ))}
            <Button asChild className="mt-3 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full">
              <a href="#reservar" onClick={() => setMobileOpen(false)}>{t(S.nav.book)}</a>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
